import { useContext, useRef, useState, useEffect } from "react";
import * as Yup from "yup";

// api
import attendanceApi from "../../api/Attendance";
import classApi from "../../api/Classes";
import staffApi from "../../api/Staff";
import studentApi from "../../api/Student";

//css
import "../dashboard/Dashboard.css";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";

//data
import {
  empty,
  errorResponse,
  getInputDateFormat,
  humanizeDate,
  isArray,
  isString,
  prepareResponseData,
} from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";
import { Toast } from "primereact/toast";
import colors from "../../config/colors";
import AttendanceWrapper from "../../components/appWrapper/AttendanceWrapper";
import { Form, Formik } from "formik";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import SelectField from "../../components/form/SelectField";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FaCheck, FaExclamationTriangle, FaTimes } from "react-icons/fa";
import InputField from "../../components/form/InputField";
import MultiSelectField from "../../components/form/MultiSelectField";
import { attendanceStatus, classGroup } from "../../data/ClassGroup";
import FullPageLoader from "../../components/loader/FullPageLoader";
import { Dialog } from "primereact/dialog";
import { useLocation } from "react-router-dom";
import ClickableList from "../../components/lists/clickableList/ClickableList";
import InputFieldChange from "../../components/form/InputFieldChange";

const validationSchema = Yup.object().shape({
  start_date: Yup.string().optional(),
  end_date: Yup.string().optional(),
  classIds: Yup.array().optional(),
  studentIds: Yup.array().optional(),
  class_group: Yup.string().optional(),
  class_categories: Yup.array().optional(),
  search: Yup.string().optional(),
});

const updateAttendanceValidationSchema = Yup.object().shape({
  time: Yup.string().optional(),
  status: Yup.string().required(),
});

// const getDaysInMonth = () => {
//   const date = new Date();
//   const year = date.getFullYear();
//   const month = date.getMonth() + 1;
//   return new Date(year, month, 0).getDate();
// };

const AttendanceDashboard = ({ ...props }) => {
  const { user } = useContext(AuthContext);

  // stat status
  const [isLoading, setIsLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classCategories, setClassCategories] = useState([]);
  const [classGroupType, setClassGroupType] = useState("");
  const [dateArray, setDateArray] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [visible, setVisible] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState({});
  const [schoolStaff, setSchoolStaff] = useState([]);
  const [studentIds, setStudentIds] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchedStudents, setSearchedStudents] = useState([]);
  const searchRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    start_date: "",
    end_date: "",
    classIds: [],
    studentIds: [],
    class_group: "",
    class_categories: [],
    search: "",
  });

  const toastTR = useRef(null);
  const formikRef = useRef();
  const location = useLocation();

  const triggerAttendanceSubmit = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  useEffect(() => {
    try {
      const handleClickOutside = (event) => {
        if (searchRef.current && !searchRef.current.contains(event.target)) {
          setSearchedStudents([]);
        }
      };

      if (!empty(searchedStudents)) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    } catch (error) {}
  }, [searchedStudents]);

  useEffect(() => {
    getClasses();
    getStaff();
    getClassCategories();

    if (!empty(location) && !empty(location.state)) {
      const paramState = !empty(location.state) ? location.state : {};
      let classId = paramState?.classId || "";
      let studentId = paramState?.studentId || "";
      if (classId) {
        setInitialValues((prev) => ({
          ...prev,
          classIds: [classId],
          class_group: "Class",
        }));
        setClassGroupType("Class");
      }
      if (studentId) {
        setInitialValues((prev) => ({ ...prev, studentIds: [studentId] }));
        setStudentIds([studentId]);
        getSelectedStudents([studentId]);
      }
    }
  }, []);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  /**
   * Get selected students
   * @returns
   */
  const getSelectedStudents = async (arrStudentIds = studentIds) => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await studentApi.getSelectedStudents(
        schoolId,
        arrStudentIds
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.response) ||
        !response_data.success
      ) {
        return setSelectedStudents([]);
      }
      const data = isArray(response_data?.response?.students)
        ? response_data.response.students
        : [];
      setSelectedStudents(data);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getStaff = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await staffApi.getStaff(schoolId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.response) ||
        !response_data.success
      ) {
        return setSchoolStaff([]);
      }
      const data = isArray(response_data?.response)
        ? response_data.response
        : [];
      setSchoolStaff(data);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const searchConstraints = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Something went wrong.",
          "Please refresh page or try again later."
        );
      }

      let startDate = !empty(values.start_date)
        ? values.start_date
        : getInputDateFormat();
      let endDate = !empty(values.end_date)
        ? values.end_date
        : getInputDateFormat();
      const classIds =
        !empty(values.classIds) && isArray(values.classIds)
          ? values.classIds
          : [];
      const classGroup = !empty(values.class_group) ? values.class_group : "";
      const classCategories =
        !empty(values.class_categories) && isArray(values.class_categories)
          ? values.class_categories
          : [];

      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await attendanceApi.getAttendanceHistory(
        schoolId,
        startDate,
        endDate,
        classIds,
        classGroup,
        classCategories,
        studentIds
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.response) ||
        !response_data.success
      ) {
        return responseDialog(
          "error",
          "Attendance History",
          "Failed to fetch attendance history."
        );
      }

      const data = isArray(response_data?.response)
        ? response_data?.response
        : [];
      const schoolDays = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
      ];

      const result = [];
      startDate = new Date(startDate);
      endDate = new Date(endDate);

      // Set current date to start date
      let currentDate = new Date(startDate);

      // Loop through dates until we reach end date
      while (currentDate <= endDate) {
        const itemDate = new Date(JSON.parse(JSON.stringify(currentDate)));
        const dayName = currentDate
          .toLocaleString("en-US", { weekday: "short" })
          .toUpperCase();
        const monthName = currentDate
          .toLocaleString("en-US", { month: "short" })
          .toUpperCase();
        const dayNumber = String(currentDate.getDate()).padStart(2, "0");
        const isSchoolDay = schoolDays.includes(
          currentDate.toLocaleString("en-US", { weekday: "long" })
        );

        result.push({
          day: dayName,
          dayNo: dayNumber,
          isSchoolDay,
          date: itemDate,
          monthName,
        });

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setDateArray(result);
      return setAttendanceHistory(data);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = (currentAttendanceData, fullName, studentId, data) => {
    try {
      const checkInTimestamp =
        currentAttendanceData?.checkInTime &&
        humanizeDate(currentAttendanceData?.checkInTime);
      const checkOutTimestamp =
        currentAttendanceData?.checkOutTime &&
        humanizeDate(currentAttendanceData?.checkOutTime);
      const checkInTimestampArr = checkInTimestamp?.split(",");
      const checkOutTimestampArr = checkOutTimestamp?.split(",");
      const checkInDate = checkInTimestampArr?.[0];
      const checkOutDate = checkOutTimestampArr?.[0];
      const checkInTime = checkInTimestampArr?.[1];
      const checkOutTime = checkOutTimestampArr?.[1];
      const addedBy = currentAttendanceData?.addedBy || "";
      const updatedBy = currentAttendanceData?.updatedBy || "";
      const currentDate = data?.date ? getInputDateFormat(data?.date) : "";

      let addedByDetails = {};
      let updatedByDetails = {};
      for (let i = 0; i < schoolStaff.length; i++) {
        if (schoolStaff?.[i]?._id === addedBy) {
          addedByDetails = {
            fullName: `${schoolStaff?.[i]?.lastName} ${schoolStaff?.[i]?.firstName} ${schoolStaff?.[i]?.middleName}`,
            id: schoolStaff?._id,
          };
        }

        if (schoolStaff?.[i]?._id === updatedBy) {
          updatedByDetails = {
            fullName: `${schoolStaff?.[i]?.lastName} ${schoolStaff?.[i]?.firstName} ${schoolStaff?.[i]?.middleName}`,
            id: schoolStaff?._id,
          };
        }
      }

      if (empty(addedByDetails) && !empty(addedBy)) {
        addedByDetails = { fullName: "School Management" };
      }

      if (empty(updatedByDetails) && !empty(updatedBy)) {
        updatedByDetails = { fullName: "School Management" };
      }

      setSelectedDayData({
        checkInDate,
        checkOutDate,
        checkInTime,
        checkOutTime,
        fullName,
        addedByDetails,
        updatedByDetails,
        attendanceId: currentAttendanceData?._id,
        studentId,
        currentDate,
        currentDateDetails: { ...data },
      });
      setVisible(true);
    } catch (error) {
      responseDialog("error", "Attendance Details", "Internal Server Error.");
    }
  };

  const attendanceBodyTemplate = (data) => (rowData) => {
    try {
      const isSchoolDay = data?.isSchoolDay || false;
      const attendanceLog = rowData?.attendanceLog || [];

      let currentAttendanceData = {};
      const isPresent = attendanceLog.find((item) => {
        if (!item?.date || !data?.date) return false;
        const itemDate = new Date(item.date);
        const targetDate = new Date(data.date);

        if (
          itemDate.getFullYear() === targetDate.getFullYear() &&
          itemDate.getMonth() === targetDate.getMonth() &&
          itemDate.getDate() === targetDate.getDate()
        ) {
          currentAttendanceData = item;
          return true;
        }
        return false;
      });

      return (
        <div
          onClick={() =>
            showModal(
              currentAttendanceData,
              rowData?.fullName,
              rowData?.studentId,
              data
            )
          }
        >
          {isSchoolDay ? (
            <div
              style={{
                textAlign: "center",
                fontSize: 12,
                minWidth: 30,
                cursor: "pointer",
              }}
            >
              {isPresent ? (
                <FaCheck style={{ color: "green" }} />
              ) : (
                <FaTimes style={{ color: "red" }} />
              )}
            </div>
          ) : (
            <div
              style={{
                fontSize: 8,
                fontWeight: "bold",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Weekend
            </div>
          )}
        </div>
      );
    } catch (error) {
      responseDialog("error", "Error Alert", `Something went wrong.`);
    }
  };

  const attendanceHeaderBodyTemplate = (data) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          fontSize: "12px",
        }}
      >
        <div>{data?.dayNo}</div>
        <div>{data?.day}</div>
        <div
          className="text-mute"
          style={{ fontSize: 10, color: "#929194", marginTop: 3 }}
        >
          ({data?.monthName})
        </div>
      </div>
    );
  };

  const handleMultiSelectChange = (name, newValue, setFieldValue) => {
    const itemIds = !empty(newValue) && isArray(newValue) ? newValue : [];
    setFieldValue(name, itemIds);
  };

  // handle attendance type
  const handleInputType = (name, setFieldValue) => (e) => {
    const value =
      !empty(e) &&
      !empty(e.target) &&
      !empty(e.target.value) &&
      isString(e.target.value)
        ? e.target.value
        : "";
    setFieldValue(name, value);
    if (name !== "status") {
      setClassGroupType(value);
    }
  };

  const getClasses = async () => {
    try {
      const response = await classApi.getClasses('yes');
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Something went wrong"
        );
      }
      const data =
        !empty(response_data) &&
        !empty(response_data.response) &&
        isArray(response_data.response)
          ? response_data.response
          : [];
      setClasses(data);
    } catch (error) {}
  };

  const getClassCategories = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getClassCategories(schoolId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        return setClassCategories([]);
      }
      const data =
        !empty(response_data) &&
        !empty(response_data.response) &&
        isArray(response_data.response)
          ? response_data.response
          : [];
      setClassCategories(data);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const updateAttendance = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Something went wrong.",
          "Please refresh page or try again later."
        );
      }

      let time = !empty(values.time) ? values.time : "";
      let date = selectedDayData?.currentDate || "";
      const status = !empty(values.status) ? values.status : [];

      if (status !== "Absent" && empty(time)) {
        return responseDialog(
          "error",
          "Validation error.",
          "Please enter a time."
        );
      }

      if (status !== "Absent" && empty(date)) {
        return responseDialog(
          "error",
          "Validation error.",
          "Please refresh the page or try again later."
        );
      }

      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await attendanceApi.updateAttendance(
        schoolId,
        time,
        date,
        status,
        selectedDayData?.attendanceId || "",
        selectedDayData?.studentId || ""
      );
      const response_data = prepareResponseData(response);
      if (!response_data.success) {
        return responseDialog(
          "error",
          "Update Attendance",
          response_data?.response ||
            response_data?.message ||
            "Failed to update attendance."
        );
      }

      responseDialog("success", "Success", "Updated attendance successfully.");
      setVisible(false);
      return triggerAttendanceSubmit();
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const updateStudentList = (item) => {
    try {
      if (isArray(selectedStudents)) {
        setSelectedStudents(selectedStudents);
      }

      item.isVisible = false;
      setSelectedStudents((prev) =>
        prev.filter((student) => student._id !== item?._id)
      );
      setStudentIds((prev) =>
        prev.filter((studentId) => !(studentId === item._id))
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const searchStudents = (setFieldValue) => (e) => {
    try {
      const value =
        !empty(e) &&
        !empty(e.target) &&
        !empty(e.target.value) &&
        isString(e.target.value)
          ? e.target.value
          : "";
      setFieldValue("search", value);

      setTimeout(async () => {
        const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
        const response = await studentApi.getSearchedStudents(schoolId, value);
        const response_data = prepareResponseData(response);
        if (!response_data?.success) {
          return;
        }

        setSearchedStudents(response_data?.response || []);
      }, 2000);
    } catch (error) {}
  };

  const AddSelectedStudent = (student) => {
    try {
      setSearchedStudents([]);
      setSelectedStudents((prev) => [...prev, student]);
      let updatedStudentIds = [];
      if (!studentIds.includes(student?._id)) {
        studentIds.push(student?._id);
        updatedStudentIds = studentIds;
      }
      setStudentIds(updatedStudentIds);
    } catch (error) {}
  };

  return (
    <>
      <AttendanceWrapper {...props}>
        <main>
          {/* menu header */}
          <MainHeader title="Attendance History" />
          {/* end menu header */}
          <div className="mb-20" style={{ width: "100%" }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={searchConstraints}
              innerRef={formikRef}
            >
              {({ values, setFieldValue }) => (
                <Form style={{ width: "100%" }}>
                  <div className="attendance-dashboard-filter">
                    <InputField
                      placeholder="Enter Start Date"
                      name="start_date"
                      height={50}
                      type="date"
                      labelTitle="Start Date"
                      containerWidth={230}
                    />
                    <InputField
                      placeholder="Enter End Date"
                      name="end_date"
                      height={50}
                      type="date"
                      labelTitle="End Date"
                      containerWidth={230}
                    />
                    <SelectField
                      labelTitle="Class Group"
                      required={true}
                      placeholder="Select Class Group"
                      name="class_group"
                      options={classGroup}
                      height={50}
                      valueKey="title"
                      selectedOption={values.class_group}
                      handleChangeFunc={handleInputType(
                        "class_group",
                        setFieldValue
                      )}
                      containerWidth={300}
                      width={300}
                    />
                    {classGroupType === "Class" && (
                      <MultiSelectField
                        labelTitle="Classes"
                        placeholder="Select Class(es)"
                        name="classIds"
                        options={classes}
                        required={true}
                        selectedOptions={values?.classIds || []}
                        valueKey="_id"
                        height={50}
                        mt="mt-10"
                        onChange={(e) =>
                          handleMultiSelectChange(
                            "classIds",
                            e.value,
                            setFieldValue
                          )
                        }
                        containerWidth={300}
                        width={300}
                      />
                    )}
                    {classGroupType === "Category" && (
                      <MultiSelectField
                        labelTitle="Classes Categories"
                        placeholder="Select Class Categories"
                        name="class_categories"
                        options={classCategories}
                        selectedOptions={values.class_categories}
                        valueKey="_id"
                        required={true}
                        height={50}
                        mt="mt-10"
                        onChange={(e) =>
                          handleMultiSelectChange(
                            "class_categories",
                            e.value,
                            setFieldValue
                          )
                        }
                        containerWidth={300}
                        width={300}
                      />
                    )}
                  </div>

                  <div style={{ position: "relative" }}>
                    <div className="attendance-dashboard-filter mt-20 attendance-search-width">
                      <InputFieldChange
                        placeholder="Enter Student`s first name, last name, reg. no."
                        name="search"
                        height={50}
                        labelTitle="Search Student"
                        onChange={searchStudents(setFieldValue)}
                        icon="search"
                      />
                    </div>
                    {!empty(searchedStudents) && (
                      <div
                        style={{
                          backgroundColor: colors.lightgray,
                          width: "60%",
                          minHeight: 200,
                          padding: "20px 35px",
                          marginLeft: "50px",
                          position: "absolute",
                          zIndex: 999,
                          overflowY: "scroll",
                        }}
                        ref={searchRef}
                      >
                        {searchedStudents.map((student, index) => {
                          return (
                            <div
                              className="mt-10 fs-14"
                              style={{
                                borderBottom: `1px solid ${colors.white}`,
                                paddingBottom: 10,
                                cursor: "pointer",
                              }}
                              onClick={() => AddSelectedStudent(student)}
                              key={index}
                            >
                              {student?.lastName} {student?.firstName} (
                              {student?.regNo} - {student?.classTitle})
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {!empty(studentIds) || !empty(selectedStudents) ? (
                    <div className="mt-20 mb-20">
                      <span className="bold" style={{ color: colors.primary }}>
                        Selected Students
                      </span>
                      <div
                        className="mt-10 name-container col-g-8 row-g-8"
                        style={{
                          flexWrap: "wrap",
                          height: "auto",
                          paddingDown: 20,
                        }}
                      >
                        {Array.isArray(selectedStudents) &&
                          selectedStudents.map((item, index) => {
                            return (
                              <ClickableList
                                visible={
                                  item?.isVisible === undefined ||
                                  item?.isVisible === null
                                    ? true
                                    : false
                                }
                                title={`${item?.lastName} ${item.firstName}`}
                                onClick={() => updateStudentList(item)}
                                key={index}
                              />
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}

                  <div>
                    {values?.class_group || !empty(selectedStudents) ? (
                      <ButtonIcon
                        height={50}
                        marginTop={3}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={150}
                        borderColor="#633ccd"
                        buttonText="Generate"
                        type="submit"
                      />
                    ) : (
                      <div></div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <div
            className="mt-40"
            style={{ display: "flex", flexDirection: "column" }}
          >
            {/* left box */}
            {attendanceHistory.map((data, idx) => {
              return (
                <div className="datatable attendance-table mb-80" key={idx}>
                  <div className="mb-10">{data?.classTitle}</div>
                  <DataTable
                    value={isArray(data?.students) ? data?.students : []}
                    tableClassName="dashboard-table"
                    responsiveLayout="scroll"
                    emptyMessage="No data found"
                    tableStyle={{ minWidth: "100%" }}
                    stripedRows
                    className="striped-table"
                    key={idx}
                  >
                    <Column
                      field="fullName"
                      header="Names"
                      headerClassName="table-header"
                      className="name-column"
                      style={{ minWidth: "250px", fontSize: 12 }}
                    />

                    {dateArray.map((data, index) => (
                      <Column
                        key={index + 1}
                        field={`day${index + 1}`}
                        header={attendanceHeaderBodyTemplate(data)}
                        headerClassName="table-header attendance-header"
                        body={attendanceBodyTemplate(data)}
                        style={{ height: 30, minWidth: "30px" }}
                        headerStyle={{ minWidth: "30px" }}
                      />
                    ))}
                  </DataTable>
                </div>
              );
            })}
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}

        <Dialog
          header="Attendance Details"
          visible={visible}
          onHide={() => {
            setSelectedDayData({});
            setVisible(!visible);
          }}
          style={{ width: "40vw" }}
          breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        >
          <div className="m-0">
            <div
              className="mb-20"
              style={{
                borderBottom: `2px dotted ${colors.primary}`,
                paddingBottom: 10,
              }}
            >
              <div>
                <strong>Name: </strong>
                {selectedDayData?.fullName}
              </div>
              <div className="mt-5">
                <strong>Date: </strong>(
                {selectedDayData?.currentDateDetails?.day}){" "}
                {selectedDayData?.currentDate
                  ? getInputDateFormat(new Date(selectedDayData?.currentDate))
                  : "--"}
              </div>
            </div>
            {!empty(selectedDayData?.checkInDate) && (
              <div>
                <div>Check-In: </div>
                <div className="mt-5">
                  <strong>Date: </strong>
                  {selectedDayData?.checkInDate || "--"}
                </div>
                <div className="mt-10">
                  <strong>Time: </strong>
                  {selectedDayData?.checkInTime || "--"}
                </div>
                <div className="mt-10">
                  <strong>By: </strong>
                  {selectedDayData?.addedByDetails?.fullName || "--"}
                </div>
              </div>
            )}

            {!empty(selectedDayData?.checkOutDate) && (
              <div className="mt-30">
                <div>Check-Out: </div>
                <div className="mt-5">
                  <strong>Date: </strong>
                  {selectedDayData?.checkOutDate || "--"}
                </div>
                <div className="mt-10">
                  <strong>Time: </strong>
                  {selectedDayData?.checkOutTime || "--"}
                </div>
                <div className="mt-10">
                  <strong>By: </strong>
                  {selectedDayData?.updatedByDetails?.fullName || "--"}
                </div>
              </div>
            )}
            <div>
              {(empty(selectedDayData?.checkInDate) ||
                empty(selectedDayData?.checkInTime)) && (
                <div
                  style={{
                    color: colors.danger,
                    textDecoration: "italic",
                    textAlign: "center",
                  }}
                >
                  <span>
                    <FaExclamationTriangle />
                  </span>
                  <br />
                  <span>
                    No attendance was recorded for{" "}
                    <strong style={{ color: colors.primary }}>
                      {selectedDayData?.fullName}
                    </strong>{" "}
                    on this day.
                  </span>
                </div>
              )}
            </div>
            <div
              className="mt-20 flex justify-center direction-column"
              style={{ padding: "20px 0" }}
            >
              <p style={{ color: colors.primary }}>
                <strong>Update Attendance Details</strong>
              </p>

              <Formik
                enableReinitialize
                initialValues={{ time: "", status: "" }}
                validationSchema={updateAttendanceValidationSchema}
                onSubmit={updateAttendance}
              >
                {({ values, setFieldValue, handleChange }) => (
                  <Form style={{ width: "100%" }}>
                    <div className="attendance-dashboard-filter">
                      <SelectField
                        labelTitle="Status"
                        required={true}
                        placeholder="Select Status"
                        name="status"
                        options={attendanceStatus}
                        height={50}
                        valueKey="title"
                        selectedOption={values.status}
                        handleChangeFunc={handleInputType(
                          "status",
                          setFieldValue
                        )}
                        containerWidth={200}
                        width={300}
                      />
                    </div>
                    {values?.status !== "Absent" && (
                      <>
                        <div className="attendance-dashboard-filter mt-20">
                          <InputField
                            placeholder="Enter Time"
                            name="time"
                            height={50}
                            type="time"
                            required={true}
                            containerWidth={200}
                            labelTitle="Time"
                            onChange={handleChange}
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <ButtonIcon
                        height={50}
                        marginTop={3}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={200}
                        borderColor="#633ccd"
                        buttonText="Submit"
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Dialog>
        <Toast ref={toastTR} position="bottom-left" />
      </AttendanceWrapper>
    </>
  );
};

export default AttendanceDashboard;
